import { focusOnFirst, trapFocus } from './focus.js';
import h from './hyperscript.js';
export default class ModalPrompt {
  constructor(selector = 'button.show-prompt', options = {}) {
    this.selector = selector;
    this.options = options = Object.assign({}, options);
    if (selector) {
      // Listen to all click events and open a modal if it is a valid link
      document.body.addEventListener('click', function (event) {
        if (this.createFromButton(event.target)) {
          event.preventDefault();
        }
      }.bind(this), {
        capture: true
      });
    }
    this.close = function (identifier) {
      ModalPrompt.destroy(identifier);
    };
    this.create = function (content, buttons = [], options = {}) {
      return ModalPrompt.create(content, buttons, options);
    };
    this.createFromButton = function (element) {
      if (!element) {
        console.error('Unable to create prompt from button, no element provided.');
        return;
      }

      // Check if it matches the selector.
      var button = element.closest(selector);
      if (!button) {
        return false;
      }

      // Get title attribute.
      let title = '';
      if (link.hasAttribute('title')) {
        title = link.getAttribute('title');
      }
      // As fallback check tooltip title.
      if (!title && link.hasAttribute('data-original-title')) {
        title = link.getAttribute('data-original-title');
      }
      let description = button.getAttribute('data-content') ? button.getAttribute('data-content') : null;

      // Render modal.
      return this.create(description, null, Object.assign(options, {
        class: button.getAttribute('data-modal-class'),
        size: link.getAttribute('data-model-size'),
        title: title
      }));
    };
  }
}
ModalPrompt.destroy = function (data) {
  // Check if element is specified.
  if (!data.element) {
    return false;
  }
  if (data.focusTrap) {
    data.focusTrap();
  }

  // Call before closed callback.
  if (data.options && data.options.onClosing && typeof data.options.onClosing === 'function') {
    data.options.onClosing(data);
  }
  const modalContent = data.element.querySelector('.modal');
  if (modalContent) {
    modalContent.dispatchEvent(new CustomEvent('modal-hidden'));
  }

  // Remove container and optionally the open class on the body.
  data.element.remove();
  if (!document.body.querySelector('.modal.show')) {
    document.body.classList.remove('modal-open');
  }
  delete data.element;

  // Call closed callback.
  if (data.options && data.options.onClose && typeof data.options.onClose === 'function') {
    data.options.onClose(data);
  }
  return true;
};
ModalPrompt.create = function (content, buttons = [], options = {}) {
  // Override default with given options.
  options = Object.assign({
    class: '',
    html: false,
    title: '',
    size: null,
    closeInHeader: false,
    onClose: null,
    onLoad: null,
    textClose: 'Sluiten'
  }, options);
  let data = {
    element: null,
    options: options,
    focusTrap: null
  };

  // Ensure at least one button exists.
  if (!buttons || buttons.length === 0) {
    buttons = [{
      title: options.textClose,
      icon: 'fas fa-chevron-right fa-fw'
    }];
  }
  let modalClose = null;
  if (options.closeInHeader) {
    modalClose = h('button', {
      'aria-label': options.textClose,
      class: 'close',
      // Remove modal from document on click.
      click: () => {
        ModalPrompt.destroy(data);
      },
      'data-dismiss': 'modal'
    }, [h('span', {
      'aria-hidden': 'true'
    }, [h('i', {
      class: 'fa-solid fa-times fa-fw'
    })])]);
  }

  // Create option elements and assign callbacks.
  buttons = buttons.map(option => {
    // Automatically convert string to button option, assuming the string is the title of the button.
    if (typeof option === 'string') {
      option = {
        title: option
      };
    } else if (!option.title) {
      // Ensure a title exists.
      option.title = options.textClose;
    }
    const buttonContent = [option.title];
    if (option.icon) {
      buttonContent.push(h('i', {
        class: option.icon
      }));
    }
    const element = h('button', {
      class: 'btn ' + (option.class ? option.class : 'btn-light'),
      // Add click listener.
      click: function () {
        // Close prompt.
        ModalPrompt.destroy(data);

        // Invoke option's callback.
        if (option.callback) {
          option.callback();
        }
        // Redirect to other location.
        if (option.href) {
          window.location = option.href;
        }
      }
    }, buttonContent);
    return element;
  });

  // Convert content to paragraphs.
  const contentElements = [];
  if (options.html) {
    contentElements.push(document.createElement('div'));
    contentElements[0].innerHTML = content;
  } else {
    const contentItems = content.split('\n');
    for (const contentItem of contentItems) {
      const paragraph = h('p', {}, '');
      paragraph.innerHTML = contentItem;
      contentElements.push(paragraph);
    }
  }
  contentElements.push(h('div', {
    class: 'd-flex justify-content-between'
  }, buttons));

  // Create modal content.
  let modalContent = [h('div', {
    class: 'modal-body'
  }, contentElements)];
  // Prepend header.
  if (modalClose || options.title) {
    modalContent.unshift(h('div', {
      class: 'modal-header'
    }, [h('div', {
      class: 'modal-title',
      role: 'heading',
      'aria-level': 2
    }, options.title ? options.title : ''), modalClose ? modalClose : '']));
  }
  // Wrap modal content.
  modalContent = h('div', {
    class: 'modal show ' + (options.class ? options.class : ''),
    'data-backdrop': 'true',
    role: 'dialog',
    style: 'display: block;',
    tabindex: '-1',
    title: options.title + ' prompt'
  }, [h('div', {
    class: 'modal-dialog modal-dialog-centered ' + (options.size ? options.size : ''),
    role: 'document'
  }, [h('div', {
    'aria-live': 'polite',
    class: 'modal-content',
    role: 'status'
  }, modalContent)])]);

  // Create modal.
  data.element = h('div', {
    class: 'modal-prompt'
  }, [h('div', {
    class: 'modal-backdrop show ' + (options.class ? options.class : '')
  }), modalContent]);

  // Add modal to body.
  document.body.append(data.element);

  // Setup Popover.
  const popoverElements = $(data.element).find('[data-toggle="popover"]');
  if (popoverElements.popover) {
    popoverElements.popover({
      container: 'body'
    });
  }
  // Setup Tooltips.
  const selectorTooltip = '[data-toggle="tooltip"]';
  const tooltipElements = $(data.element).find(selectorTooltip);
  if (tooltipElements.tooltip) {
    tooltipElements.tooltip({
      container: 'body'
    });
  }
  const tooltipButtons = Array.from(data.element.querySelectorAll(selectorTooltip));
  const setupTooltipButton = button => {
    button.addEventListener('click', () => {
      const tooltipElements = $(button);
      if (tooltipElements.tooltip) {
        tooltipElements.tooltip('hide');
      }
    });
  };
  for (let i = 0; i < tooltipButtons.length; i++) {
    setupTooltipButton(tooltipButtons[i]);
  }
  data.focusTrap = trapFocus(data.element);
  focusOnFirst(data.element.querySelector('.modal-body'));

  // Call loaded callback.
  if (options.onLoad && typeof options.onLoad === 'function') {
    options.onLoad();
  }
  modalContent.dispatchEvent(new CustomEvent('modal-show'));
  modalContent.dispatchEvent(new CustomEvent('modal-shown'));

  // Return data.
  return data;
};